import type {
  OutletJoinRefusalCode,
  SaleRefusalCode,
  ShiftRefusalCode,
  TerminalActivationOutcome,
  TerminalStatus,
  TicketRefusalCode,
} from '@repo/desktop-bridge';

/**
 * What a device-local refusal says to the person standing at the till.
 *
 * The same separation `error-messages.ts` makes for the API's codes, applied to
 * the codes this *device* raises: a screen does not word a refusal, it looks one
 * up. These are not the API's and must not be confused with them — every code
 * here is decided on the terminal, against what it holds, and **nothing has been
 * printed and nothing has been queued** when one comes back.
 *
 * The wording rule for a till, from `docs/ui-conventions.md`: say what happened
 * and what to do next, and never alarm somebody mid-queue about something that
 * is working as designed. There is a visitor waiting at the counter for every
 * one of these sentences, so each names the shortest way forward it honestly
 * can, and none of them says "try again" where trying again cannot work.
 */

const SHIFT_MESSAGES: Record<ShiftRefusalCode, string> = {
  /**
   * One message for an unknown staff code and for a wrong PIN, and it stays that
   * way. Separating them would hand whoever is holding the device a roster of
   * who works at this outlet — the same enumeration `AUTH_CREDENTIALS_INVALID`
   * closes in the back office, closed here for the same reason.
   */
  SHIFT_CREDENTIALS_INVALID: 'That staff code and PIN were not accepted. Check both and try again.',

  SHIFT_LOCKED_OUT:
    'Too many failed attempts. This staff code is blocked at this till for 15 minutes. Wait, or ask a manager to sign in instead.',

  /**
   * The device has never fetched a roster, or holds one for an outlet it no
   * longer stands at. Either way there is nothing here to check a PIN against,
   * and no amount of retyping changes it — so this names the network rather than
   * the credential, which is what is actually wrong.
   */
  SHIFT_NO_ROSTER:
    'This till has no staff list yet, so it cannot check a PIN. Connect it to the park network for a moment and try again.',

  SHIFT_TERMINAL_UNAVAILABLE:
    'This till is not registered, or its registration has run out. An owner needs to register the device in the back office before it can take money.',

  /** Only ever the answer to a switch (#254), which words it with the outlet's name below. */
  SHIFT_NOT_ON_ROSTER: 'You are not on the staff list at this outlet.',
};

const TICKET_MESSAGES: Record<TicketRefusalCode, string> = {
  TICKET_NO_ACTING_SHIFT: 'Nobody is signed in at this till. Sign in before selling.',

  TICKET_TERMINAL_UNAVAILABLE:
    'This till is not registered, or its registration has run out, so it cannot record a sale. Nothing was printed and nothing was charged. An owner needs to register the device in the back office.',

  /**
   * Named as the network rather than as a fault. A till with no catalogue has
   * never reached the API once — the held copy survives an outage indefinitely
   * and is never invalidated by age — so this is a device that has not been set
   * up rather than one that has gone wrong.
   */
  TICKET_NO_CATALOGUE:
    'This till has not yet been told what it sells. Connect it to the park network for a moment, then try again.',

  TICKET_ITEM_UNKNOWN:
    'That item is no longer on this till’s list — the list was replaced while the screen was open. Nothing was printed. Choose again from the buttons as they stand now.',

  /**
   * Should be unreachable: the picker draws admission only. If somebody sees it,
   * the screen and the held copy disagreed, which is a bug rather than a rule —
   * so it says plainly that this is not the till for it instead of implying the
   * cashier chose wrongly.
   */
  TICKET_ITEM_NOT_ADMISSION:
    'That is a shop item, not admission, so it cannot be printed as a ticket. Nothing was printed. Report this — it should not be on this screen.',

  /**
   * Should be unreachable: the field is not offered on cash and is cleared when
   * the cashier switches to it. Worded for the person rather than for the bug,
   * because the remedy is the same either way — the same sentence
   * `SALE_PAYMENT_REFERENCE_NOT_ALLOWED` gives, said about a ticket.
   */
  TICKET_PAYMENT_REFERENCE_NOT_ALLOWED:
    'A cash ticket cannot carry a payment reference. Nothing was printed. Clear the reference, or change how they paid.',

  TICKET_NOT_QUEUED:
    'The sale could not be saved on this device, so nothing was printed and nothing should be charged. Report this before selling anything else.',
};

/**
 * A refused basket, which needs its own sentences rather than admission's.
 *
 * Every one of these is decided on the device before a number is spent, so each
 * can honestly say **nothing was printed and nothing should be charged** — which
 * is the one thing a cashier with a customer in front of them needs to hear
 * first. None of them says "try again" where trying again cannot work.
 *
 * The API's own refusals — `SALES_PRICE_MISMATCH`, `SALES_TOTAL_MISMATCH`,
 * `SALES_NUMBER_CONFLICT` and the rest — are deliberately not here. A sale is
 * queued rather than sent, so those arrive when the outbox drains, long after
 * the customer has walked away; they are worded in `error-messages.ts` and read
 * on the refused-writes screen, which is where somebody can actually act on
 * them.
 */
const SALE_MESSAGES: Record<SaleRefusalCode, string> = {
  SALE_NO_ACTING_SHIFT: 'Nobody is signed in at this till. Sign in before selling.',

  SALE_TERMINAL_UNAVAILABLE:
    'This till is not registered, or its registration has run out, so it cannot record a sale. Nothing was printed and nothing was charged. An owner needs to register the device in the back office.',

  /**
   * Named as the network rather than as a fault, exactly as the ticket screen's
   * is: a till with no catalogue has never reached the API once, and the held
   * copy is never invalidated by age.
   */
  SALE_NO_CATALOGUE:
    'This till has not yet been told what it sells. Connect it to the park network for a moment, then try again.',

  /**
   * Reachable by pressing Take payment with nothing in the basket, so it is
   * worded as the ordinary mistake it is rather than as a fault. The API would
   * answer `SALES_EMPTY_BASKET`, but it never hears about this one.
   */
  SALE_EMPTY_BASKET:
    'There is nothing in the basket yet. Tap what they are buying, then take payment.',

  SALE_ITEM_UNKNOWN:
    'Something in the basket is no longer on this till’s list — the list was replaced while the screen was open. Nothing was printed. Clear the basket and ring it up again from the buttons as they stand now.',

  /**
   * Should be unreachable: the picker draws shop items only. If somebody sees
   * it, the screen and the held copy disagreed, which is a bug rather than a
   * rule — so it says so instead of implying the cashier chose wrongly.
   */
  SALE_ITEM_NOT_GOODS:
    'Something in the basket is admission with no GST on its price, which is issued as a ticket rather than sold on a tax invoice. Nothing was printed and nothing was charged. Report this — the till should have sent it as a ticket.',

  SALE_QUANTITY_INVALID:
    'One of the quantities is not a number this till can sell. Nothing was printed. Set it back to a whole number of items and try again.',

  /**
   * The honest sentence for a total too large to carry, which in practice means
   * a quantity somebody typed by accident. It does not say "too expensive",
   * because the limit is arithmetic rather than a price ceiling the park set.
   */
  SALE_AMOUNT_UNREPRESENTABLE:
    'That basket comes to more than this till can add up, so nothing was printed and nothing should be charged. Check the quantities — one of them is far larger than it looks.',

  /**
   * Should be unreachable: the field is not offered on cash and is cleared when
   * the cashier switches to it. It is worded for the person rather than for the
   * bug, because the remedy is the same either way.
   */
  SALE_PAYMENT_REFERENCE_NOT_ALLOWED:
    'A cash sale cannot carry a payment reference. Nothing was printed. Clear the reference, or change how they paid.',

  SALE_NOT_QUEUED:
    'The sale could not be saved on this device, so nothing was printed and nothing should be charged. Report this before selling anything else.',

  /**
   * A taxed item, and no invoice header on this till (#269) — the park identity
   * is unset, or has not reached it. Says what to do, and that the rest of the
   * shelf still sells: the choice was to refuse only what cannot be invoiced.
   * The till asks for the header as this is shown, so pressing again once the
   * owner has set it is the whole remedy. Short, because at 1000x625 a longer
   * one pushed the basket it is about out of the column.
   */
  SALE_TAX_INVOICE_UNAVAILABLE:
    'Nothing was charged: this till cannot print a tax invoice until the owner sets the GSTIN under Tax identities. Items without GST still sell.',

  /**
   * This outlet asks for a time slot (#277) and the sale carried none. The
   * screen requires both times, so reaching this is the screen and the main
   * process disagreeing — said plainly, with the remedy.
   */
  SALE_TIME_SLOT_REQUIRED:
    'Nothing was charged: this counter needs the visitor’s time slot. Enter both times and charge again.',
};

export const messageForShiftRefusal = (reason: ShiftRefusalCode): string => SHIFT_MESSAGES[reason];

/**
 * Why the counter could not move to `outlet` with the cashier still signed in
 * (#254). The counter has stayed where it was, so each says so by not saying
 * otherwise, and names the one thing that would change the answer.
 */
export const messageForSwitchRefusal = (
  reason: Extract<TerminalActivationOutcome, { activated: false }>['reason'],
  outlet: string,
): string => {
  switch (reason) {
    case 'SHIFT_NOT_ON_ROSTER':
      return `You are not on the staff list at ${outlet}, so this till cannot sell there under your name. Only an owner can add you to it, in the back office.`;
    case 'SHIFT_NO_ROSTER':
      return `This till has no staff list for ${outlet} yet. Connect it to the park network for a moment and try again.`;
    case 'SHIFT_TERMINAL_UNAVAILABLE':
      return `This till’s registration for ${outlet} has run out or been withdrawn. An owner needs to register it again in the back office.`;
    case 'REGISTRATION_NOT_HELD':
      return `This till no longer sells for ${outlet}.`;
  }
};

/**
 * Why `outlet` cannot join the sale at the counter (#284) — said when the
 * cashier reaches for it, before any money is taken. The roster's refusals are
 * a switch's, word for word: joining admits a cashier exactly as switching
 * does, and the two screens must not disagree about who may sell where.
 */
export const messageForJoinRefusal = (reason: OutletJoinRefusalCode, outlet: string): string => {
  switch (reason) {
    case 'SALE_NO_ACTING_SHIFT':
      return SALE_MESSAGES.SALE_NO_ACTING_SHIFT;
    case 'SALE_NO_CATALOGUE':
      return `This till has not yet been told what ${outlet} sells. Connect it to the park network for a moment, then try again.`;
    case 'SALE_NO_ADMISSION':
      return `${outlet} sells no tickets or activities. Switch the counter to ${outlet} to sell from its shop.`;
    case 'SALE_NO_TAXED_ADMISSION':
      return `${outlet}’s admission has no GST, so its tickets print on their own and cannot share a payment with this sale. Switch the counter to ${outlet} to sell them.`;
    case 'SALE_TAX_INVOICE_UNAVAILABLE':
      return `This till cannot print a tax invoice for ${outlet} until the owner sets the GSTIN under Tax identities, so ${outlet} cannot join this sale.`;
    default:
      return messageForSwitchRefusal(reason, outlet);
  }
};

/**
 * Why a line was not added to a basket that spans outlets (#284). An untaxed
 * admission is a ticket, not a sale line, and a basket across outlets is one
 * sale per outlet — so the two cannot share the one payment.
 */
export const UNTAXED_ACROSS_OUTLETS =
  'An admission with no GST on its price prints as a ticket, so it cannot be paid for together with another outlet’s sale. Take payment for one before ringing up the other.';

export const messageForTicketRefusal = (reason: TicketRefusalCode): string =>
  TICKET_MESSAGES[reason];

export const messageForSaleRefusal = (reason: SaleRefusalCode): string => SALE_MESSAGES[reason];

/**
 * What a failed IPC call says.
 *
 * Not a refusal — the main process did not answer at all, which on a terminal
 * means the shell is in trouble rather than the network. Distinct from every
 * message above because none of the remedies there applies.
 */
export const TILL_UNAVAILABLE =
  'This till’s software did not respond. Nothing was printed. Restart the terminal, and report it if it happens again.';

/**
 * A party sent to the till with no answer back (#253).
 *
 * `TILL_UNAVAILABLE` says nothing was printed, and for a party that cannot be
 * known: the till sells admissions one after another and may have printed some
 * before it stopped answering. What *is* known is that pressing again is safe,
 * because the basket is sent under the same key and the till never sells an
 * admission twice under one — so that is what this says.
 */
export const TILL_UNAVAILABLE_FOR_PARTY =
  'This till’s software did not answer, so it cannot say whether this party was sold. Press Try again — nothing already sold will be sold twice. If it still does not answer, restart the terminal and count the tickets that printed.';

/**
 * What a browser is told.
 *
 * The till screen is reachable in a browser — its real gate is the lease and the
 * shift, neither of which a browser session can express — and what it does there
 * is say so plainly rather than render a sale it could never complete.
 */
export const NOT_A_TILL =
  'This is a till screen, and this is not a till. Tickets are sold on the gate and outlet terminals, which run the desktop app and hold the device’s registration.';

/**
 * What a till says about its own standing when that standing is not `live`.
 *
 * **The one refusal on this screen nobody has to type a PIN to earn.** Before
 * issue #215 a device whose lease had been withdrawn rendered the ordinary
 * sign-on form: the words for the state existed — `SHIFT_TERMINAL_UNAVAILABLE`
 * above says them — but they arrived only after a cashier had entered a staff
 * code and a PIN and been refused, and they named a back office the screen
 * offered no way to reach. These are said first instead, and the screen that
 * says them carries the way on.
 *
 * Three states and not one message, against the general rule of preserving the
 * API's non-distinctions. That rule is about refusals the *server* deliberately
 * collapses — an unknown staff code and a wrong password are one code because
 * separating them enumerates who works here. Nothing is enumerated by a device
 * saying which of its own three standings it is in, and they are three different
 * days: one is waiting to be set up, one wants the network, and one is not
 * coming back without an owner. Saying "not registered, or its registration has
 * run out" to all three sends somebody to look for the wrong problem.
 *
 * `Record` over `Exclude<TerminalStatus, 'live'>` rather than a `switch`, so a
 * fifth standing added to the seam fails `pnpm typecheck` here rather than
 * rendering a blank margin at a gate.
 */
export interface TerminalStandingCopy {
  /** The state itself, as the margin's heading. */
  readonly heading: string;
  /** The value of the *Registration* fact: one or two words on a plate. */
  readonly registration: string;
  /** What happened, and what puts it right. */
  readonly explanation: string;
}

export const TERMINAL_OUT_OF_SERVICE: Record<
  Exclude<TerminalStatus, 'live'>,
  TerminalStandingCopy
> = {
  /**
   * The explanation is the sentence this screen has always shown for an
   * unregistered device, kept word for word. It was the one standing of the
   * three that was already worded, already true, and already in the right voice.
   */
  unregistered: {
    heading: 'Not registered.',
    registration: 'None yet',
    explanation:
      'A till cannot take money until an owner has registered it to an outlet in the back office. Until then it does not know where it stands, and nothing it sold could be recorded.',
  },

  /**
   * Named as the network first, because that is what it usually is and it is the
   * remedy somebody standing at the counter can actually carry out. The back
   * office is the second sentence rather than the first for the same reason.
   */
  expired: {
    heading: 'Registration has run out.',
    registration: 'Ran out',
    explanation:
      'A terminal renews its registration whenever it reaches the park network, and this one has not renewed in time. Put it back on the network and it will renew itself. If it will not, an owner can register the device again in the back office.',
  },

  /**
   * **The one that does not recover on its own**, and it says so rather than
   * leaving somebody to retry into it. It also says where the remedy has to
   * happen: a registration only ever reaches a device through the machine it is
   * for, so "in the back office" means *on this screen's own machine*, which is
   * the fact that makes the button beside it worth having.
   */
  revoked: {
    heading: 'Withdrawn from service.',
    registration: 'Withdrawn',
    explanation:
      'An owner withdrew this terminal’s registration in the back office, so it can no longer take money and nothing entered here will change that. An owner can put it back into service by registering the device again — from this machine, because a registration only ever reaches a device through the machine it is for.',
  },
};

/**
 * The consequence, stated once for all three standings.
 *
 * It is the same sentence in every one of them and it is the thing a cashier has
 * to know, so it sits under the heading the way *every ticket you sell is
 * recorded against you* sits under `Sign on.` — the promoted line, not chrome.
 */
export const TILL_OUT_OF_SERVICE = 'Nothing can be sold at this counter until it is put right.';
